// The Scorecard page on `#interviews/<pk>/<app>` (lane B7 DC-171…174, rebuilt by B14 to the Corporate Interview
// Playbook, brief §2.1): the page interviewers reach from My Interviews. It hosts the ONE playbook frame
// (PlaybookScorecard) that `#packets/<pk>/<app>` hosts too, so the two routes cannot diverge. This host owns the read,
// the draft state, Save (and a quiet autosave once the author pauses), Submit and the print of the saved card; the frame
// owns every page, the lock, the rubric and the Gate. Hard rules stay on the server (Law 3): the offered options, the
// lock, the average core score, the rubric band and the open items are its answers, never a count made here. Once
// submitted the page is the same pages read-only. On the Hiring Manager's own round the Candidate decision card follows.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  packetListMine, packetPrint, scorecardOpen, scorecardSaveDraft, scorecardSubmit, type ScorecardOpenOutput,
} from "@/api/tools";
import { AppError } from "@/api/runtime";
import { errorText, isClientRefusal, useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { V1Slot } from "@/shell/island";
import { SavedPrint, type SavedRendition } from "@/ui/SavedPrint";
import {
  Button, Card, COMMON_LABELS, EmptyState, ErrorStrip, McRoot, NAV_ITEM_LABELS, Pill, RecordPage, Recorded, SCORECARD_STATUS, Stack, formatDate,
  type RecordNeighbours, type RecordSection,
} from "@/mc";
import { recordNav, useTrail } from "@/shell/recordTrail";
import { bandRows, interviewRows } from "./Interviews";
import { CandidateDecisionCard, type CandidateDecision } from "./CandidateDecisionCard";
import { SCORE_LABELS } from "./labels";
import { NotesRail, PlaybookScorecard } from "./PlaybookScorecard";
import { dimensionsOf, draftOf, playbookOf, missingOf, revisionDraft, submitBody, withSaved, type Draft, type MissingItem } from "./playbook";

const SUBMITTED = new Set(["submitted", "frozen", "frozen_with_addenda"]);

function decidedOf(card: Record<string, unknown>): CandidateDecision | null {
  const value = card["candidate_decision"];
  return value === "advance" || value === "hold" || value === "pass" ? value : null;
}

/** A refusal's words by the field it names (the server's field names are the frame's field ids, C1). */
export function fieldErrorsOf(error: unknown): Record<string, string> {
  return error instanceof AppError ? { ...error.fieldErrors } : {};
}

/** F-185: the record header a routed Scorecard page draws (Back to the list it came from, its neighbours there). */
export interface ScorecardNav {
  section: RecordSection;
  neighbours: RecordNeighbours;
}

export function ScorecardView({ card, viewerId, canDecide, packetId, nav }: { card: ScorecardOpenOutput; viewerId: string | null; canDecide: boolean; packetId?: string | undefined; nav?: ScorecardNav | undefined }) {
  const raw = card as unknown as Record<string, unknown>;
  const dims = useMemo(() => dimensionsOf(card), [card]);
  const submitted = SUBMITTED.has(card.status);
  const author = card.projection === "author";
  const editable = author && !submitted && card.can["save_draft"] !== false;
  const [draft, setDraft] = useState<Draft>(() => submitted
    ? revisionDraft(dims, card.revision, card.ratings)
    : draftOf(dims, card.draft));
  // the draft version the next save expects, read at call time (a save and the autosave timer never send a stale one)
  const version = useRef(card.draft_version);
  const [saved, setSaved] = useState<Record<string, unknown> | null>(null);
  const [recorded, setRecorded] = useState(false);
  const [refused, setRefused] = useState<unknown>(null);
  const [printed, setPrinted] = useState<SavedRendition | null>(null);
  const dirty = useRef(false);
  const timer = useRef<number | null>(null);
  useEffect(() => { version.current = card.draft_version; }, [card.draft_version]);
  useEffect(() => { if (submitted) setDraft(revisionDraft(dims, card.revision, card.ratings)); }, [submitted, card.revision, card.ratings, dims]);
  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);
  const ids = { scorecard_id: card.scorecard_id, interview_id: card.interview_id, application_id: card.application_id };
  const save = useWrite(scorecardSaveDraft, [["scorecard"]]);
  const submit = useWrite(scorecardSubmit, [["scorecard"], ["packets"], ["debriefs"]]);
  const print = useWrite((input: Parameters<typeof packetPrint>[0]) => packetPrint(input));
  const latest = useRef(draft);
  latest.current = draft;

  const onSave = async () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    setRefused(null);
    try {
      const out = await save.mutateAsync({ ...ids, draft: latest.current, expected_version: version.current });
      version.current = out.draft_version;
      setSaved(out as unknown as Record<string, unknown>);
      dirty.current = false;
    } catch (e) {
      setRefused(e);
    }
  };
  const onChange = (next: Draft) => {
    setDraft(next);
    latest.current = next;
    dirty.current = true;
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { if (dirty.current && !save.isPending) void onSave(); }, 2000);
  };
  const onSubmit = async () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    setRefused(null);
    try {
      await submit.mutateAsync({ ...ids, draft: submitBody(latest.current, "draft") });
      dirty.current = false;
      setRecorded(true);
    } catch (e) {
      setRefused(e);
    }
  };
  const hmRound = /(^|_)hm(_|$)/.test(card.round_kind) && card.author_id === viewerId;
  const shown = withSaved(card, saved);
  const missing: MissingItem[] = editable ? missingOf((shown as unknown as Record<string, unknown>)["missing"]) : [];
  const errors = fieldErrorsOf(refused);
  const refusal = refused && Object.keys(errors).length === 0 ? errorText(refused as AppError) : null;
  const printAction = submitted && author && packetId ? (
    <Button sm busy={print.isPending}  onClick={() => { void print.mutateAsync({ packet_id: packetId, application_id: card.application_id, blank: false }).then(setPrinted).catch(() => undefined); }}>
      {COMMON_LABELS.print}
    </Button>
  ) : undefined;
  const body = (
    <Stack gap={16}>
      {recorded ? <Recorded live>{SCORECARD_STATUS.submitted}</Recorded> : null}
      <PlaybookScorecard
        open={shown}
        draft={draft}
        onChange={onChange}
        mode={submitted ? "readonly" : "draft"}
        editable={editable && !submit.isPending}
        missing={missing}
        onSave={editable ? () => { void onSave(); } : undefined}
        onSubmit={editable ? () => { void onSubmit(); } : undefined}
        saveBusy={save.isPending}
        submitBusy={submit.isPending}
        errors={errors}
        refusal={refusal}
        printAction={printAction}
        framed={nav !== undefined}
        notesRail={author ? <NotesRail value={draft.private_notes} disabled={!editable} onChange={editable ? (value) => onChange({ ...draft, private_notes: value }) : undefined} /> : undefined}
      />
      {print.error ? <ErrorStrip message={errorText(print.error)} /> : null}
      <V1Slot><SavedPrint title={SCORE_LABELS.scorecard} value={printed} kind="packet" onClose={() => setPrinted(null)} /></V1Slot>
      {hmRound ? (
        <CandidateDecisionCard
          requisitionId={card.requisition_id}
          applicationId={card.application_id}
          submitted={submitted}
          decided={decidedOf(raw)}
          can={canDecide}
          reasonText={draft.justification ?? ""}
        />
      ) : null}
    </Stack>
  );
  if (!nav) return body;
  // F-185 (F-179): candidate · title · round · date · status, Back, and Previous / Next across the person's interviews
  const role = playbookOf(card).guide?.header.target_role ?? null;
  return (
    <RecordPage
      section={nav.section}
      record={card.candidate}
      neighbours={nav.neighbours}
      hs={{ back: "score.back", previous: "score.previous", next: "score.next" }}
      title={card.candidate}
      pills={<Pill tone={submitted ? "ok" : "info"}>{submitted ? SCORECARD_STATUS.submitted : SCORECARD_STATUS.inProgress}</Pill>}
      meta={[role, card.round_label, card.held_at ? formatDate(card.held_at) : null].filter((fact): fact is string => Boolean(fact))}
      action={printAction}
    >
      {body}
    </RecordPage>
  );
}

/** A Scorecard page the server will not open (a withdrawn candidate's cancelled round, a card no longer the viewer's):
 * the packet pane's not-found state — the lock, the page's name and the server's sentence — never a skeleton that
 * never settles; anything else is the error strip with Retry (demo smoke D6). */
export function ScorecardUnavailable({ error, onRetry }: { error: AppError; onRetry?: (() => void) | undefined }) {
  if (isClientRefusal(error)) return <EmptyState revoked sub={errorText(error)}>{SCORE_LABELS.scorecard}</EmptyState>;
  return <ErrorStrip message={errorText(error)} onRetry={onRetry} />;
}

/** Back to the list the scorecard was opened from and its neighbours there; else Interviews, in the list's own order. */
function useScorecardNav(packetId: string, applicationId: string, viewerId: string | null, enabled: boolean): ScorecardNav {
  const trail = useTrail();
  const mine = useRead(["packets", "mine"], () => packetListMine({}), { enabled });
  const ordered = useMemo(() => {
    if (!mine.data) return [];
    const { open, done } = bandRows(interviewRows(mine.data, viewerId).filter((row) => row.own));
    return [...open, ...done].map((row) => `#interviews/${row.packetId}/${row.applicationId}`);
  }, [mine.data, viewerId]);
  const self = `#interviews/${packetId}/${applicationId}`;
  return recordNav(trail, self, (href) => /^#(interviews|packets)\/(?!debriefs)[^/]+\/[^/?]+$/.test(href), { label: NAV_ITEM_LABELS.interviews, href: "#interviews" }, ordered);
}

function ScorecardBody({ packetId, applicationId }: { packetId: string; applicationId: string }) {
  const { me, can } = useSession();
  const nav = useScorecardNav(packetId, applicationId, me?.person_id ?? null, can("packet.list_mine"));
  const open = useRead(["scorecard", packetId, applicationId], () => scorecardOpen({ packet_id: packetId, application_id: applicationId }));
  if (open.error) return <ScorecardUnavailable error={open.error} onRetry={() => void open.refetch()} />;
  if (open.isLoading || !open.data) return <Card loading />;
  return (
    <ScorecardView
      key={`${open.data.scorecard_id ?? applicationId}:${open.data.status}`}
      card={open.data}
      viewerId={me?.person_id ?? null}
      canDecide={can("candidate.decide_advance")}
      packetId={packetId}
      nav={nav}
    />
  );
}

export function ScorecardPage({ packetId, applicationId }: { packetId: string; applicationId: string }) {
  return <McRoot><ScorecardBody packetId={packetId} applicationId={applicationId} /></McRoot>;
}
